import { callApi, IApiErrorResponse } from '@/api/callApi';
import { API_ENDPOINT, API_URL } from '@/api/constant';
import ErrorMessage from '@/components/common/ErrorMessage';
import FormButtons from '@/components/common/Form/FormButtons';
import CardLayout from '@/components/Layout/CardLayout';
import FieldLayout from '@/components/Layout/FieldLayout';
import Layout from '@/components/Layout/Layout';
import BackButton from '@/components/ui/BackButton';
import Label from '@/components/ui/Label';
import PrimaryInput from '@/components/ui/PrimaryInput';
import SearchSelectBox, {
  ISelectOption,
} from '@/components/ui/SearchSelectBox';
import {
  IAddAssetForm,
  IAssetCategoryResponse,
  IAssetDetailResponse,
  IFileAsset,
} from '@/interfaces/assets.model';
import { showToastNotification } from '@/utility/helper';
import { errorTypes } from '@/utility/validation';
import { useCallback, useEffect, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import CrossIcon from '@/components/Icons/CrossIcon';
import { IDeleteResponse } from '@/interfaces/common.model';
import moment from 'moment';
import CrossIconSm from '@/components/Icons/CrossIconSm';

const AddAsset = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const { id } = useParams();
  const {
    handleSubmit,
    register,
    control,
    setValue,
    reset,
    formState: { errors },
  } = useForm<IAddAssetForm>();
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [existingImages, setExistingImages] = useState<IFileAsset[]>([]);

  const getAsset = useCallback(async () => {
    try {
      const response: IAssetDetailResponse = await callApi(
        API_URL + API_ENDPOINT.getAsset + `?assetId=${id}`,
        {
          method: 'GET',
        },
      );
      if (response.status) {
        reset({
          name: response.data.name,
          assetCategoryId: response?.data.assetCategoryId,
          status: Number(response.data.status),
          price: response?.data?.price,
          description: response?.data?.description,
          serialNo: response?.data?.serialNo,
          purchaseDate: response?.data?.purchaseDate?.split('T')[0],
        });
        setExistingImages(response.data.documents || []);
      }
    } catch (error) {
      if (error && typeof error === 'object' && 'message' in error) {
        showToastNotification('danger', (error as IApiErrorResponse).message);
      } else {
        showToastNotification('danger', 'Something went wrong');
      }
    }
  }, [id, reset]);
  useEffect(() => {
    if (id) {
      getAsset();
    }
  }, [id, getAsset]);
  const getAssetCategory = async (
    inputValue?: string,
  ): Promise<ISelectOption[]> => {
    let api = API_URL + API_ENDPOINT.getAssetsCategory;
    if (inputValue) {
      api = `${API_URL}${API_ENDPOINT.getAssetsCategory}`;
    }
    try {
      const response: IAssetCategoryResponse = await callApi(api, {
        method: 'GET',
      });
      if (response.status && response.data?.assetCategory?.length) {
        return response.data.assetCategory.map((shift) => ({
          value: shift.id,
          label: shift.name,
        }));
      }

      return [];
    } catch (error) {
      console.error('Error fetching shifts:', error);
      return [];
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const filesArray = Array.from(e.target.files);
      const validFiles: File[] = [];

      filesArray.forEach((file) => {
        if (!file.type.startsWith('image/')) {
          showToastNotification('danger', 'Only image files are allowed');
          return;
        }
        if (file.size > 5 * 1024 * 1024) {
          showToastNotification('danger', 'File size should be less than 5MB');
          return;
        }
        validFiles.push(file);
      });
      setSelectedFiles((prev) => [...prev, ...validFiles]);
      setValue('files', [...selectedFiles, ...validFiles]);
    }
  };

  const removeFile = (index: number) => {
    const updatedFiles = selectedFiles.filter((_, i) => i !== index);
    setSelectedFiles(updatedFiles);
    setValue('files', updatedFiles);
  };

  const onSubmit: SubmitHandler<IAddAssetForm> = async (data) => {
    const formData = new FormData();

    Object.keys(data).forEach((key) => {
      const dataKey = key as keyof IAddAssetForm;
      if (dataKey === 'files') return;
      const value = data[dataKey];
      if (value !== null && value !== undefined) {
        formData.append(key, String(value));
      }
    });

    if (!id) {
      formData.append('status', '1'); // only for new assets
    }
    selectedFiles.forEach((file) => {
      formData.append('files', file);
    });
    setIsLoading(true);
    try {
      const response: IDeleteResponse = await callApi(
        id
          ? API_URL + API_ENDPOINT.updateAssets + `?assetId=${id}`
          : API_URL + API_ENDPOINT.addAssets,
        {
          method: 'POST',
          multipart: true,
          data: formData,
        },
      );
      if (response?.status) {
        showToastNotification('success', response?.message);
        navigate(-1);
        reset();
      }
    } catch (error: unknown) {
      if (error && typeof error === 'object' && 'message' in error) {
        showToastNotification('danger', (error as IApiErrorResponse).message);
      } else {
        showToastNotification('danger', 'Something went wrong');
      }
    } finally {
      setIsLoading(false);
    }
  };
  return (
    <Layout>
      <div className="content container-fluid">
        <BackButton isEdit={id ? true : false} label={'Asset'} />
        <form onSubmit={handleSubmit(onSubmit)}>
          <CardLayout>
            <div className="row">
              <div className="row">
                <FieldLayout fieldStyle="col-lg-6">
                  <PrimaryInput
                    type={'text'}
                    id="name"
                    placeholder="Enter name of asset"
                    label="Name"
                    errors={errors?.name?.message}
                    required={true}
                    {...register('name', {
                      ...errorTypes.required('name'),
                    })}
                  />
                </FieldLayout>
                <FieldLayout fieldStyle="col-lg-6">
                  <Label required={true} id="companyId">
                    Select Asset Category
                  </Label>
                  <Controller
                    control={control}
                    name="assetCategoryId"
                    rules={errorTypes.required('Asset Category')}
                    render={({ field }) => (
                      <SearchSelectBox
                        value={field.value}
                        title="Select Asset Category"
                        onChange={(option) =>
                          field.onChange(option?.value || '')
                        }
                        isAsync
                        fetchOptions={getAssetCategory}
                        defaultFetch
                        disable={isLoading}
                        isUsingFilter
                      />
                    )}
                  />
                  <ErrorMessage message={errors?.assetCategoryId?.message} />
                </FieldLayout>
                <FieldLayout fieldStyle="col-lg-6">
                  <PrimaryInput
                    type={'text'}
                    id="serialno."
                    placeholder="Enter serial no."
                    label="Serial no."
                    errors={errors?.serialNo?.message}
                    required={true}
                    {...register('serialNo', {
                      ...errorTypes.required('serial no.'),
                      // ...errorTypes.serialNumber,
                    })}
                  />
                </FieldLayout>
                <FieldLayout fieldStyle="col-lg-6">
                  <PrimaryInput
                    type="text"
                    id="cost"
                    placeholder="Enter cost"
                    label="Cost"
                    errors={errors?.price?.message}
                    required={true}
                    onInput={(e: React.FormEvent<HTMLInputElement>) => {
                      e.currentTarget.value = e.currentTarget.value.replace(
                        /[^0-9.]/g,
                        '',
                      );
                    }}
                    {...register('price', {
                      ...errorTypes.required('cost'),
                      valueAsNumber: true,
                    })}
                  />
                </FieldLayout>
                {/* <FieldLayout fieldStyle="col-lg-6">
                  <Label required={true} id="status">
                    Status
                  </Label>
                  <select
                    className="form-select min-w-20 pe-5"
                    {...register('status', {
                      ...errorTypes.required('status'),
                    })}
                  >
                    <option value="" disabled>
                      Select Status
                    </option>
                    {assetStatus?.map((status) => (
                      <option value={status?.value}>{status?.label}</option>
                    ))}
                  </select>
                </FieldLayout> */}
                <FieldLayout fieldStyle="col-lg-6">
                  <PrimaryInput
                    label="Purchase Date"
                    required={true}
                    type="date"
                    id="purchaseDate"
                    {...register('purchaseDate', {
                      ...errorTypes.required('purchase date'),
                    })}
                    max={moment().format('YYYY-MM-DD')}
                    errors={errors?.purchaseDate?.message}
                  />
                </FieldLayout>
                <FieldLayout>
                  <label htmlFor="description" className="form-label">
                    Description <span className="text-danger">*</span>
                  </label>
                  <textarea
                    className={`form-control ${errors.description ? 'is-invalid' : ''}`}
                    rows={3}
                    id="description"
                    placeholder="Enter Description here"
                    {...register('description', {
                      required: 'Description is required',
                    })}
                  />
                  <ErrorMessage message={errors?.description?.message} />
                </FieldLayout>
                <FieldLayout>
                  <Label id="assetPhotos">Asset Images</Label>
                  <div className="d-flex flex-wrap gap-4">
                    <div
                      className="position-relative d-flex align-items-center justify-content-center border rounded"
                      style={{
                        width: '100px',
                        height: '100px',
                        cursor: 'pointer',
                        borderStyle: 'dashed',
                      }}
                    >
                      <input
                        type="file"
                        multiple
                        accept="image/*"
                        className="position-absolute w-100 h-100 opacity-0 cursor-pointer"
                        onChange={handleFileChange}
                      />
                      <span className="fs-1 text-secondary">+</span>
                    </div>
                    {existingImages?.map((file, index) => (
                      <div
                        key={`existing-${index}`}
                        className="position-relative"
                        style={{ width: '100px', height: '100px' }}
                      >
                        <img
                          src={file.url}
                          alt={`existing-${index}`}
                          className="w-100 h-100 object-fit-cover rounded"
                        />
                        <button
                          type="button"
                          className="btn btn-sm btn-danger position-absolute top-0 end-0 p-0 d-flex align-items-center justify-content-center rounded-circle"
                          style={{
                            width: '20px',
                            height: '20px',
                            transform: 'translate(30%, -30%)',
                          }}
                          onClick={() => {
                            const updated = existingImages.filter(
                              (_, i) => i !== index,
                            );
                            setExistingImages(updated);
                          }}
                        >
                          <CrossIcon />
                        </button>
                      </div>
                    ))}
                    {selectedFiles.map((file, index) => (
                      <div
                        key={index}
                        className="position-relative"
                        style={{ width: '100px', height: '100px' }}
                      >
                        <img
                          src={URL.createObjectURL(file)}
                          alt={`preview-${index}`}
                          className="w-100 h-100 object-fit-cover rounded"
                        />
                        <button
                          type="button"
                          className="btn btn-sm btn-danger position-absolute top-0 end-0 p-0 d-flex align-items-center justify-content-center rounded-circle"
                          style={{
                            width: '20px',
                            height: '20px',
                            transform: 'translate(30%, -30%)',
                          }}
                          onClick={() => removeFile(index)}
                        >
                          {/* <CrossIcon-sm /> */}
                          <CrossIconSm />
                        </button>
                      </div>
                    ))}
                  </div>
                </FieldLayout>
              </div>
              <FormButtons isLoading={isLoading} buttonStyle="gap-3 mt-4 mb-3">
                {id ? 'Edit Asset' : 'Add Asset'}
              </FormButtons>
            </div>
          </CardLayout>
        </form>
      </div>
    </Layout>
  );
};
export default AddAsset;
